import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Driver.js-powered guided tours for the action-plan wizard (Steps 2 & 3),
 * matching the old app's tours: auto-run on first visit, a "Revoir le tutoriel"
 * replay link, and a scroll-to-top when the tour ends. Steps whose target
 * isn't in the DOM are skipped (as the legacy tours do via `if (el)`).
 */
export interface TourStep {
  selector: string;
  title: string;
  description: string;
  side?: "top" | "bottom" | "left" | "right";
}

interface TourLabels {
  next: string;
  prev: string;
  done: string;
  progress: string; // e.g. "{{current}} / {{total}}"
}

function runTour(steps: TourStep[], labels: TourLabels, onDone?: () => void) {
  const present = steps.filter((s) => document.querySelector(s.selector));
  if (present.length === 0) return;
  const driveSteps: DriveStep[] = present.map((s) => ({
    element: s.selector,
    popover: {
      title: s.title,
      description: s.description,
      side: s.side ?? "bottom",
    },
  }));
  const d = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayColor: "rgba(0,0,0,0.55)",
    nextBtnText: labels.next,
    prevBtnText: labels.prev,
    doneBtnText: labels.done,
    progressText: labels.progress,
    steps: driveSteps,
    onDestroyed: () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDone?.();
    },
  });
  d.drive();
}

/**
 * Tours already auto-run in this page load. Dismissing tells the server, but the
 * answer only reaches the client on the next fetch of the plan — and leaving the
 * step and coming back remounts the hook meanwhile. Without this the tour
 * reappears every time, which reads as "I closed it and it keeps coming back".
 */
const autoRunOnce = new Set<string>();

/**
 * Auto-runs the tour once when `autoRun` becomes true (first visit), calling
 * `onAutoDismiss` when it ends so the server marks it seen. Returns `replay`
 * for the "Revoir le tutoriel" link (no re-dismiss on replay).
 */
export function useStepTour({
  buildSteps,
  autoRun,
  autoRunKey,
  onAutoDismiss,
}: {
  buildSteps: () => TourStep[];
  autoRun: boolean;
  /** Identifies this tour (step + plan) for the once-per-page-load guard. */
  autoRunKey: string;
  onAutoDismiss?: () => void;
}) {
  const { t } = useTranslation();

  const labels = (): TourLabels => ({
    next: t("actionPlans.tour.next"),
    prev: t("actionPlans.tour.prev"),
    done: t("actionPlans.tour.done"),
    progress: t("actionPlans.tour.progress"),
  });

  useEffect(() => {
    if (!autoRun || autoRunOnce.has(autoRunKey)) return;
    // Let the step's DOM paint before measuring highlight rects. The cleanup
    // clears the pending timer, so React 18 StrictMode's double-invoke in dev
    // still results in exactly one tour (no ref guard — that would suppress it).
    const id = window.setTimeout(() => {
      autoRunOnce.add(autoRunKey);
      runTour(buildSteps(), labels(), onAutoDismiss);
    }, 400);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, autoRunKey]);

  const replay = () => runTour(buildSteps(), labels());
  return { replay };
}
